import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import {
  Button, Dropdown, Icon, Message, Modal,
} from 'semantic-ui-react';
import { useStore } from '../../app/stores/store';
import { useTranslation } from 'react-i18next';
import { TournamentDto } from '../../app/models/Tournament';

export const RegisterToTournamentModal = observer(function RegisterToTournamentModal({
  tournament,
}: { tournament: TournamentDto }) {
  const { tournamentStore, teamStore } = useStore();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [selectedTeamId, setSelectedTeamId] = useState('');

  useEffect(() => {
    if (open && teamStore.myTeams.length === 0) teamStore.loadMyTeams();
  }, [open]);

  const eligibleTeams = teamStore.myTeams.filter(
    t_ => t_.status === 'Active' && t_.sportID === tournament.sportId && t_.isCurrentUserCaptain,
  );

  const handleRegister = async () => {
    if (!selectedTeamId) return;
    await tournamentStore.registerTeam(tournament.id, selectedTeamId);
    setOpen(false);
  };

  // Key matches what the store uses: `reg:register:${teamId}`
  const isRegistering = tournamentStore.isSubmitting(`reg:register:${selectedTeamId}`);

  return (
    <>
      <Button color="green" size="small" style={{ borderRadius: 50 }} onClick={() => setOpen(true)}>
        <Icon name="shield" />{t('tournament.register')}
      </Button>

      <Modal open={open} onClose={() => setOpen(false)} size="small" closeIcon>
        <Modal.Header>
          <Icon name="shield" color="green" />
          {t('tournament.registerModal.title')}
        </Modal.Header>
        <Modal.Content>
          {eligibleTeams.length === 0 ? (
            <Message warning>
              <Icon name="exclamation triangle" />
              {t('tournament.registerModal.noEligibleTeam')}
            </Message>
          ) : (
            <div>
              <label style={{ display: 'block', fontWeight: 700, marginBottom: 8 }}>
                {t('tournament.registerModal.selectTeam')}
              </label>
              <Dropdown
                fluid selection
                options={eligibleTeams.map(t_ => ({
                  key: t_.id, value: t_.id, text: t_.name,
                  image: { avatar: true, src: t_.logoUrl || '/assets/team-placeholder.png' },
                }))}
                value={selectedTeamId}
                onChange={(_, d) => setSelectedTeamId(d.value as string)}
                placeholder={t('tournament.registerModal.selectPlaceholder')}
              />
              {tournament.registrationMode === 'ManualReview' && (
                <Message info size="small" style={{ marginTop: 10 }}>
                  <Icon name="clock" />
                  {t('tournament.registerModal.manualReviewNote')}
                </Message>
              )}
              {tournament.entryFee != null && tournament.entryFee > 0 && (
                <Message warning size="small" style={{ marginTop: 10 }}>
                  <Icon name="euro" />
                  {t('tournament.registerModal.entryFeeNote', { fee: tournament.entryFee })}
                  <br /><strong>{t('tournament.registerModal.contactOrganizer')}: {tournament.organizerPhone}</strong>
                </Message>
              )}
            </div>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button basic onClick={() => setOpen(false)}>{t('tournament.cancel')}</Button>
          {eligibleTeams.length > 0 && (
            <Button
              positive
              loading={isRegistering}
              disabled={!selectedTeamId || tournamentStore.submitting}
              onClick={handleRegister}
            >
              <Icon name="check" />{t('tournament.registerModal.confirm')}
            </Button>
          )}
        </Modal.Actions>
      </Modal>
    </>
  );
});